import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  notification,
  Row,
  Select,
  Timeline,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  STORAGE_DELIVERY_CITY,
  STORAGE_DELIVERY_COUNTRY,
  STORAGE_DELIVERY_HOUSENUMBER,
  STORAGE_DELIVERY_HOUSENUMBER_ADDITION,
  STORAGE_DELIVERY_STREET,
  STORAGE_DELIVERY_ZIP,
} from "../../constants/LocalStorageConstants";
import styled from "styled-components";
import { lookupAddress } from "../../services/api/postcode";
import { COUNTRY_BE, COUNTRY_NL } from "../../constants/CountryConstants";
import {
  validateAddition,
  validateHouseNumber,
  validateZipCode,
} from "../../helpers/validators";
import { SmileOutlined } from "@ant-design/icons";
import { deliveryRegionFor } from "../../helpers/region";
import { DELIVERY_REGION } from "../../constants/DeliveryConstants";

/**
 * De stappen die de klant na het invullen van zijn adres te zien krijgt.
 *
 * Tot september 2026 stonden hier twee varianten naast elkaar
 * (`nextStepsLandelijk` en `nextStepsRegional`), omdat West-Friesland met eigen
 * gekoelde bussen op woensdag/donderdag/vrijdag tussen 14:00 en 18:00 werd
 * gereden en de rest van het land door een vervoerder tussen 17:00 en 22:00.
 * Tussen 23 september en 15 oktober 2026 ging alles via SooCool en was er een
 * verhaal. Sinds HVC-83 rijdt de eigen bus weer in West-Friesland, dus zijn er
 * weer twee: `nextSteps` (SooCool, met eigen tijdvak; maandag Trunkrs) en
 * `nextStepsRegional` (de bus, woensdag t/m vrijdag 14:00-18:00). Alles blijft
 * diepgevroren, ook regionaal.
 */
const nextSteps = {
  nl: {
    title: "🥩 Zo wordt jouw bestelling geleverd:",
    steps: [
      {
        title: "Vacuum verpakt per artikel",
        description:
          "Elk stuk vlees wordt zorgvuldig per artikel vacuüm verpakt aan de hand van het door jou bestelde gewicht voor maximale versheid. Zo ontvang jij ieder stukje vlees op de veiligste en optimale manier.",
      },
      {
        title: "Vriesvers ingevroren",
        description:
          "Na het verpakken wordt het vlees direct diepgevroren, zodat smaak en kwaliteit behouden blijven.",
      },
      {
        title: "Verzending in geïsoleerde vriesdoos",
        description:
          "De bestelling wordt geleverd in een stevige, geïsoleerde doos met icepacks, die het vlees diepgevroren houdt tot aan het moment van ontvangst.",
      },
      {
        title: "Bezorging aan huis",
        description:
          "Wij bezorgen van maandag t/m zaterdag door heel Nederland en in Vlaanderen en Brussel. Dinsdag t/m zaterdag met onze bezorgpartner SooCool, en dan kies je tijdens het afrekenen zelf het tijdvak; op maandag bezorgt Trunkrs, 's avonds. Via de track & trace volg je de bezorging tot aan de deur.",
        dot: <SmileOutlined />,
      },
    ],
  },
  en: {
    title: "🥩 How your order is delivered:",
    steps: [
      {
        title: "Packed vacuum packed per item",
        description:
          "Each piece of meat is carefully vacuum packed according to the weight you ordered for maximum freshness. So you get each piece of meat in the safest and most optimal way.",
      },
      {
        title: "Frozen solid",
        description:
          "After packing, the meat is frozen immediately to preserve taste and quality.",
      },
      {
        title: "Shipped in an insulated freezer box",
        description:
          "The order is delivered in a sturdy, insulated box with ice packs that keeps the meat frozen until the moment you receive it.",
      },
      {
        title: "Home delivery",
        description:
          "We deliver from Monday to Saturday throughout the Netherlands and to Flanders and Brussels. Tuesday to Saturday with our delivery partner SooCool, and then you choose the time window yourself at checkout; on Mondays Trunkrs delivers, in the evening. The track & trace lets you follow the delivery to your door.",
        dot: <SmileOutlined />,
      },
    ],
  },
};

const nextStepsRegional = {
  nl: {
    title: nextSteps.nl.title,
    steps: [
      nextSteps.nl.steps[0],
      nextSteps.nl.steps[1],
      {
        title: "Bezorging met onze eigen bus",
        description:
          "In West-Friesland bezorgen we zelf. Je bestelling komt diepgevroren mee met onze eigen bezorgbus.",
      },
      {
        title: "Bezorging aan huis",
        description:
          "Wij bezorgen op woensdag, donderdag en vrijdag tussen 14:00 en 18:00 door heel West-Friesland. Bestel je uiterlijk de avond ervoor om 23:59, dan staat je bestelling de volgende dag voor de deur. Zodra hij onderweg is, krijg je een e-mail met een track & trace.",
        dot: <SmileOutlined />,
      },
    ],
  },
  en: {
    title: nextSteps.en.title,
    steps: [
      nextSteps.en.steps[0],
      nextSteps.en.steps[1],
      {
        title: "Delivery with our own van",
        description:
          "In West-Friesland we deliver ourselves. Your order comes frozen in our own delivery van.",
      },
      {
        title: "Home delivery",
        description:
          "We deliver on Wednesday, Thursday and Friday between 14:00 and 18:00 throughout West-Friesland. Order by 23:59 the evening before and your order is at your door the next day. As soon as it is on its way, you receive an email with a track & trace.",
        dot: <SmileOutlined />,
      },
    ],
  },
};

const DeliveryAddressModal = ({ open, onClose, onSaved }) => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [zip, setZip] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [houseNumberAddition, setHouseNumberAddition] = useState("");
  const [addressLine, setAddressLine] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [allowUnknownAddition, setAllowUnknownAddition] = useState(false);
  const [countryCode, setCountryCode] = useState(COUNTRY_NL);
  const [currentStep, setCurrentStep] = useState(0);
  const [resolvedAddress, setResolvedAddress] = useState(null);

  useEffect(() => {
    if (open && typeof window !== "undefined") {
      setCurrentStep(0);
      const storedCountry =
        localStorage.getItem(STORAGE_DELIVERY_COUNTRY) || COUNTRY_NL;
      const storedZip = localStorage.getItem(STORAGE_DELIVERY_ZIP) || "";
      const storedHnr =
        localStorage.getItem(STORAGE_DELIVERY_HOUSENUMBER) || "";
      const storedAdd =
        localStorage.getItem(STORAGE_DELIVERY_HOUSENUMBER_ADDITION) || "";
      const storedStreet = localStorage.getItem(STORAGE_DELIVERY_STREET) || "";
      const storedCity = localStorage.getItem(STORAGE_DELIVERY_CITY) || "";

      setCountryCode(storedCountry);
      setZip(storedZip);
      setHouseNumber(storedHnr);
      setHouseNumberAddition(storedAdd);
      setAllowUnknownAddition(false);
      setAddressLine(null);
      setResolvedAddress(null);

      // Initialize form values
      form.setFieldsValue({
        country: storedCountry,
        zip: storedZip,
        house_number: storedHnr,
        house_number_addition: storedAdd || "",
      });

      // If we already have a stored full address, show it immediately
      if (
        storedCountry === COUNTRY_NL &&
        validateZipCode(storedZip, storedCountry) &&
        validateHouseNumber(storedHnr)
      ) {
        if (storedStreet || storedCity) {
          setResolvedAddress({
            street: storedStreet,
            city: storedCity,
            houseNumber: storedHnr,
            houseNumberAddition: storedAdd || undefined,
          });
          setAddressLine(
            `${storedStreet} ${storedHnr}${storedAdd ? ` ${storedAdd}` : ""}, ${storedCity}`,
          );
        } else {
          // No stored street/city -> try resolving immediately so user can just save
          getAddressLine(storedZip, storedHnr, storedAdd);
        }
      }
    }
  }, [open]);

  const saveAndNext = async () => {
    try {
      const values = await form.validateFields();
      const { country, zip, house_number, house_number_addition } = values;

      // For NL we require a resolved addressLine from lookup, for BE we allow without
      if (country === COUNTRY_NL && !addressLine) {
        messageApi.error(t("modal.invalid_address_combination_short"));
        return;
      }

      localStorage.setItem(STORAGE_DELIVERY_COUNTRY, country);
      localStorage.setItem(STORAGE_DELIVERY_ZIP, zip.trim());
      if (!!house_number?.trim())
        localStorage.setItem(STORAGE_DELIVERY_HOUSENUMBER, house_number.trim());
      // Persist house number addition (empty string if not provided)
      localStorage.setItem(
        STORAGE_DELIVERY_HOUSENUMBER_ADDITION,
        (house_number_addition || "").trim(),
      );
      // Persist street/city from postcode lookup (or clear if not available)
      localStorage.setItem(
        STORAGE_DELIVERY_STREET,
        (resolvedAddress?.street || "").trim(),
      );
      localStorage.setItem(
        STORAGE_DELIVERY_CITY,
        (resolvedAddress?.city || "").trim(),
      );
      if (onSaved)
        onSaved(
          country,
          zip.trim(),
          house_number.trim(),
          resolvedAddress?.street || "",
          resolvedAddress?.city || "",
          (house_number_addition || "").trim(),
        );
      // Do not close here; go to step 2 (timeline)
      setCurrentStep(1);
    } catch (e) {
      // errors shown by form
    }
  };

  const getAddressLine = async (zip, houseNumber, addition) => {
    try {
      if (countryCode !== COUNTRY_NL) return; // Only NL lookup supported
      const address = await lookupAddress({
        zipCode: zip,
        houseNumber: houseNumber,
        addition: addition ?? "",
      });

      if (address) {
        setResolvedAddress(address);
        setAddressLine(
          `${address.street} ${address.houseNumber}${!!address.houseNumberAddition ? ` ${address.houseNumberAddition}` : ""}, ${address.city}`,
        );

        // If user entered an addition but lookup doesn't return one, prompt user
        if (
          !!addition &&
          !address.houseNumberAddition &&
          !allowUnknownAddition
        ) {
          notification.warning({
            message: t("errors.addition_not_found"),
            description: t("errors.addition_not_found_description", {
              number: houseNumber,
              addition: addition,
            }),
            key: "addition-modal",
            closeIcon: <div />,
            btn: (
              <div style={{ display: "flex", flexFlow: "wrap", gap: 6 }}>
                <Button
                  onClick={() => {
                    setHouseNumberAddition("");
                    // Reset address line without addition
                    setAddressLine(`${address.street} ${address.houseNumber}`);
                    setResolvedAddress({
                      ...address,
                      houseNumberAddition: undefined,
                    });
                    form.setFieldsValue({
                      house_number_addition: "",
                    });
                    notification.destroy("addition-modal");
                  }}
                  type="primary"
                >
                  {t("remove_addition")}
                </Button>
                <Button
                  onClick={() => {
                    setAllowUnknownAddition(true);
                    // Keep user's entered addition in display
                    setAddressLine(
                      `${address.street} ${address.houseNumber} ${addition}`,
                    );
                    setResolvedAddress({
                      ...address,
                      houseNumberAddition: addition,
                    });
                    notification.destroy("addition-modal");
                  }}
                  style={{ marginRight: 4 }}
                  type="primary"
                >
                  {t("keep_addition", { number: houseNumber, addition })}
                </Button>
              </div>
            ),
            duration: 0,
          });
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <StyledModal
      title={currentStep === 0 ? t("delivery_details_modal_title") : null}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={currentStep === 0 ? 600 : 750}
    >
      {contextHolder}
      {currentStep === 0 && (
        <>
          <p className={"description"}>
            {t("delivery_details_modal_description")}
          </p>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(_, all) => {
              // Keep state in sync for lookup logic
              if (all.country && all.country !== countryCode) {
                setCountryCode(all.country);
                localStorage.setItem(STORAGE_DELIVERY_COUNTRY, all.country);
                setAddressLine(null);
              }
              if (typeof all.zip !== "undefined") setZip(all.zip || "");
              if (typeof all.house_number !== "undefined")
                setHouseNumber(all.house_number || "");
              if (typeof all.house_number_addition !== "undefined")
                setHouseNumberAddition(all.house_number_addition || "");

              // Trigger lookup for NL when inputs look valid enough
              const c = all.country || countryCode;
              const z = typeof all.zip !== "undefined" ? all.zip : zip;
              const hn =
                typeof all.house_number !== "undefined"
                  ? all.house_number
                  : houseNumber;
              const add =
                typeof all.house_number_addition !== "undefined"
                  ? all.house_number_addition
                  : houseNumberAddition;
              if (
                c === COUNTRY_NL &&
                validateZipCode(z, c) &&
                validateHouseNumber(hn)
              ) {
                getAddressLine(z, hn, add);
              } else if (c === COUNTRY_BE) {
                setAddressLine(null);
              }
            }}
          >
            <Row gutter={12}>
              <Col xs={24}>
                <Form.Item
                  name="country"
                  label={t("country")}
                  rules={[
                    { required: true, message: t("errors.required_field") },
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <Select
                    options={[
                      { value: COUNTRY_NL, label: t("netherlands") },
                      { value: COUNTRY_BE, label: t("belgium") },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="zip"
                  label={t("zip_code")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_valid_zip_code"),
                    },
                    {
                      validator: (_, value) =>
                        validateZipCode(
                          value,
                          form.getFieldValue("country") || countryCode,
                        )
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(t("form.enter_valid_zip_code")),
                            ),
                    },
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <Input
                    placeholder={t("zip_code")}
                    maxLength={
                      (form.getFieldValue("country") || countryCode) ===
                      COUNTRY_BE
                        ? 4
                        : 7 /* allow a space for NL autocomplete like '1234 AB' */
                    }
                    onChange={(e) => {
                      const c = form.getFieldValue("country") || countryCode;
                      const raw = e.target.value;
                      const normalized =
                        c === COUNTRY_BE
                          ? raw.replace(/[^0-9]/g, "").trim()
                          : raw.replace(/\s+/g, "").toUpperCase().trim();
                      form.setFieldsValue({ zip: normalized });
                    }}
                    onBlur={() => {
                      const c = form.getFieldValue("country") || countryCode;
                      const raw = form.getFieldValue("zip") || "";
                      const normalized =
                        c === COUNTRY_BE
                          ? (raw || "").replace(/[^0-9]/g, "").trim()
                          : (raw || "")
                              .replace(/\s+/g, "")
                              .toUpperCase()
                              .trim();
                      form.setFieldsValue({ zip: normalized });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={14} md={8}>
                <Form.Item
                  name="house_number"
                  label={t("house_number")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_house_number"),
                    },
                    {
                      validator: (_, value) =>
                        validateHouseNumber(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(t("form.enter_valid_house_number")),
                            ),
                    },
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <Input
                    placeholder={t("house_number")}
                    onChange={(e) =>
                      form.setFieldsValue({
                        house_number: e.target.value.replace(/[^0-9]/g, ""),
                      })
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={10} md={8}>
                <Form.Item
                  name="house_number_addition"
                  label={t("house_number_addition")}
                  rules={[
                    {
                      validator: (_, value) =>
                        validateAddition(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                t("form.enter_valid_house_number_addition"),
                              ),
                            ),
                    },
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <Input
                    placeholder={t("house_number_addition")}
                    maxLength={2}
                    onChange={(e) =>
                      form.setFieldsValue({
                        house_number_addition: e.target.value.toUpperCase(),
                      })
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          {addressLine && (
            <div style={{ marginBottom: 12 }}>
              <p>{addressLine}</p>
            </div>
          )}
          <Button
            type="primary"
            block
            onClick={saveAndNext}
            style={{ marginTop: addressLine ? 0 : 12 }}
          >
            {t("save")}
          </Button>
        </>
      )}
      {currentStep === 1 && (
        <div style={{ marginTop: 8 }}>
          {(() => {
            const lang = (i18n?.language || "nl").startsWith("nl")
              ? "nl"
              : "en";
            const regional =
              deliveryRegionFor({ zip, country: countryCode }) ===
              DELIVERY_REGION.REGIONAL;
            const data = (regional ? nextStepsRegional : nextSteps)[lang];
            return (
              <>
                <h3 style={{ fontSize: 18, color: "#000" }}>{data.title}</h3>
                <Timeline
                  style={{ marginTop: 24 }}
                  items={data.steps.map((s, index) => ({
                    color: "green",
                    dot: s.dot,
                    children: (
                      <div>
                        <strong>{s.title}</strong>
                        <div style={{ color: "#666" }}>{s.description}</div>
                      </div>
                    ),
                  }))}
                />
              </>
            );
          })()}
          <Button
            style={{ marginTop: 12 }}
            type="primary"
            block
            onClick={onClose}
          >
            {t("close")}
          </Button>
        </div>
      )}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-header {
    margin-bottom: 0;
  }

  .description {
    margin-bottom: 16px;
  }
`;

export default DeliveryAddressModal;
